import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

interface BannerProps { }

const StartTestimonials: React.FC<BannerProps> = () => {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [slidesToShow, setSlidesToShow] = useState<number>(3);
  const [isAutoPlaying, setIsAutoPlaying] = useState<boolean>(true);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const apiValues: any = useSelector(
    (state: any) => state.common.commonData?.allTestimonials?.topdata
  );
  const apiValues2: any = useSelector(
    (state: any) => state.common.commonData?.allTestimonials?.allVideosTestimonials
  );

  // Fallback data if API data not available
  const fallbackData = [
    {
      id: 1,
      videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
      thumbnailUrl: 'assets/img/palani-bg-gray.jpg',
      title: 'Student Testimonial 1',
      description: 'Amazing learning experience'
    },
    {
      id: 2,
      videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
      thumbnailUrl: 'assets/img/palani-bg-gray.jpg',
      title: 'Student Testimonial 2',
      description: 'Great platform for education'
    },
    {
      id: 3,
      videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
      thumbnailUrl: 'assets/img/palani-bg-gray.jpg',
      title: 'Student Testimonial 3',
      description: 'Highly recommended'
    },
    {
      id: 4,
      videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
      thumbnailUrl: 'assets/img/palani-bg-gray.jpg',
      title: 'Student Testimonial 4',
      description: 'Excellent course content'
    },
    {
      id: 5,
      videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
      thumbnailUrl: 'assets/img/palani-bg-gray.jpg',
      title: 'Student Testimonial 5',
      description: 'Great instructors'
    },
    {
      id: 6,
      videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
      thumbnailUrl: 'assets/img/palani-bg-gray.jpg',
      title: 'Student Testimonial 6',
      description: 'Professional development'
    }
  ];

  const testimonialData = Array.isArray(apiValues2) && apiValues2.length > 0 ? apiValues2 : fallbackData;

  // Handle responsive slides
  useEffect(() => {
  const handleResize = () => {
    const width = window.innerWidth;
    if (width >= 1280) {       // ✅ large desktop screens
      setSlidesToShow(4);
    } else if (width >= 1024) {
      setSlidesToShow(3);
    } else if (width >= 768) {
      setSlidesToShow(2);
    } else {
      setSlidesToShow(1);
    }
  };

  handleResize();
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);


  // Calculate pagination
  const totalPages = Math.ceil(testimonialData.length / slidesToShow);
  const currentItems = testimonialData.slice(
    currentPage * slidesToShow,
    (currentPage + 1) * slidesToShow
  );

  // Pause all videos
  const pauseAllVideos = useCallback(() => {
    videoRefs.current.forEach(video => {
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });
    setPlayingIndex(null);
  }, []);

  // Auto-play functionality
  const startAutoPlay = useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    
    autoPlayRef.current = setInterval(() => {
      if (playingIndex === null) {
        setCurrentPage(prev => (prev + 1) % totalPages);
        pauseAllVideos();
      }
    }, 3000);
  }, [totalPages, playingIndex, pauseAllVideos]);

  const stopAutoPlay = useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  }, []);

  // Start/stop autoplay based on state
  useEffect(() => {
    if (isAutoPlaying && playingIndex === null) {
      startAutoPlay();
    } else {
      stopAutoPlay();
    }

    return () => stopAutoPlay();
  }, [isAutoPlaying, playingIndex, startAutoPlay, stopAutoPlay]);

  // Handle play video
  const handlePlayVideo = (index: number) => {
    pauseAllVideos();
    setPlayingIndex(index);
    setIsAutoPlaying(false);
    stopAutoPlay();
  };

  // Handle video end/pause
  const handleVideoEnd = useCallback(() => {
    setPlayingIndex(null);
    setIsAutoPlaying(true);
  }, []);

  const handleVideoPause = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (video.paused && !video.seeking && video.currentTime > 0) {
      setPlayingIndex(null);
      setIsAutoPlaying(true);
    }
  }, []);

  // Navigation handlers
  const goToPage = (page: number) => {
    pauseAllVideos();
    setCurrentPage(page);
  };

  const nextPage = () => {
    pauseAllVideos();
    setCurrentPage(prev => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    pauseAllVideos();
    setCurrentPage(prev => (prev - 1 + totalPages) % totalPages);
  };

  // Grid classes based on slidesToShow
const getGridClasses = () => {
  switch (slidesToShow) {
    case 1:
      return 'grid-cols-1';
    case 2:
      return 'grid-cols-1 md:grid-cols-2';
    case 3:
      return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    case 4:
    default:
      return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'; // ✅ 4 slides on xl+
  }
};


  return (
    <div className="testimonials-section testimonials-sec-one text-center">
      <div className="container mx-auto px-4" style={{ overflow: "hidden" }}>
        <div className="section-header text-center mb-8">
          <span className="fw-medium text-secondary text-decoration-underline mb-2 d-inline-block">
            {apiValues?.title || "Student Testimonials"}
          </span>
          <h2 className='text-[#1D1753] text-3xl font-bold mb-4'>{apiValues?.description || "What Our Students Say"}</h2>
          <p className='text-[#1D1753] text-lg'>{apiValues?.miniDescription || "Real feedback from our community"}</p>
        </div>

        {/* Grid-based Slider */}
        <div className="relative">
          {/* Navigation Arrows */}
          {totalPages > 1 && (
            <>
              <button
                onClick={prevPage}
                className="absolute -left-12 xl:-left-16 lg:-left-12 md:-left-8 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center hover:bg-gray-50 hover:shadow-xl transition-all duration-200"
                aria-label="Previous testimonials"
              >
                <span className="text-gray-600 text-xl font-bold">‹</span>
              </button>
              <button
                onClick={nextPage}
                className="absolute -right-12 xl:-right-16 lg:-right-12 md:-right-8 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center hover:bg-gray-50 hover:shadow-xl transition-all duration-200"
                aria-label="Next testimonials"
              >
                <span className="text-gray-600 text-xl font-bold">›</span>
              </button>
            </>
          )}

          {/* Grid Container with Animation */}
          <div className="overflow-hidden">
            <div 
              className={`grid ${getGridClasses()} gap-3 transition-all duration-500 ease-in-out`}
              key={currentPage} // Force re-render for animation
            >
              {currentItems.map((item, index) => {
                const actualIndex = currentPage * slidesToShow + index;
                return (
                  <div
                    key={item.id}
                    className="animate-fade-in"
                    style={{
                      animationDelay: `${index * 100}ms`,
                      animationFillMode: 'both'
                    }}
                  >
                    <div className="relative w-full  bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-xl">
                      {playingIndex === actualIndex ? (
                        item.videoUrl.includes("youtube.com") ? (
                          <iframe
                            className="w-full h-full"
                            src={`${item.videoUrl}${item.videoUrl.includes('?') ? '&' : '?'}autoplay=1&rel=0`}
                            title={`video-${actualIndex}`}
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                          />
                        ) : (
                          <video
                            ref={(el) => { videoRefs.current[actualIndex] = el; }}
                            className="w-full h-full object-cover"
                            src={item.videoUrl}
                            autoPlay
                            controls
                            playsInline
                            preload="metadata"
                            onPause={handleVideoPause}
                            onEnded={handleVideoEnd}
                            onError={() => {
                              console.error(`Video failed to load: ${item.videoUrl}`);
                              setPlayingIndex(null);
                              setIsAutoPlaying(true);
                            }}
                          />
                        )
                      ) : (
                        <div className="relative w-full h-full group cursor-pointer" onClick={() => handlePlayVideo(actualIndex)}>
                          <img 
                            src={item.thumbnailUrl} 
                            alt={item.title} 
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-between text-white p-6">
                            {/* Content at top */}
                            <div className="text-left">
                              <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">{item.title}</h3>
                              <p className="text-sm text-gray-200 line-clamp-3">{item.description}</p>
                            </div>
                            
                            {/* Play button centered */}
                            <div className="flex justify-center items-center absolute inset-0">
                              <div className="bg-[#21439F]/90 hover:bg-[#21439F] text-white font-semibold rounded-full w-20 h-20 flex items-center justify-center transition-all duration-200 shadow-2xl hover:shadow-3xl hover:scale-110 backdrop-blur-sm">
                                <span className="text-3xl ml-1">▶</span>
                              </div>
                            </div>

                            {/* Subtle overlay on hover */}
                            <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dots Navigation */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-8 space-x-3">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToPage(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentPage === index 
                      ? 'bg-[#21439F] scale-125 shadow-lg' 
                      : 'bg-gray-300 hover:bg-gray-400 hover:scale-110'
                  }`}
                  aria-label={`Go to page ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Auto-play Controls */}
        {/* <div className="flex justify-center mt-6">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className={`text-sm px-4 py-2 rounded-full transition-all duration-200 ${
              isAutoPlaying && playingIndex === null
                ? 'text-white bg-[#21439F] hover:bg-[#1a3580]' 
                : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
            }`}
          >
            {isAutoPlaying && playingIndex === null ? '⏸ Pause Auto-play' : '▶ Resume Auto-play'}
          </button>
        </div> */}
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default StartTestimonials;